import React, { FC, useContext, useState } from 'react';
 
import { ContextApi } from 'react-uforms';
 
import { useLoadScript, GoogleMap } from '@react-google-maps/api';
 
import { Question } from '@ch/common/types/data';
 
import { FormInput } from '@ch/common/components/form/input';
 
import { addressPopulationObject } from './const';
 
import { QuizContext } from './quiz-context';
 
import { loadFile } from './helpers/file';
 
import { useLang } from '../use-lang';
 
interface AutocompleteInputProps extends Question {
  name: string;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
}
 
const libraries = ['places' as googleLibraries];
 
type googleLibraries = 'places' | 'drawing' | 'geometry' | 'localContext' | 'visualization';
 
const QuizFieldAutocompleteInput: FC<AutocompleteInputProps> = ({ name, placeholder, disabled, onPlaceSelected }) => {
  const { lang } = useLang();
 
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_API_KEY}`,
    language: lang,
    libraries,
  });
 
  const { getErrors } = useContext(ContextApi);
  const { userQuestions } = useContext(QuizContext);
 
  // Add state to hold country info for display
  const [countryInfo, setCountryInfo] = useState<{longName?: string, shortName?: string}>({});
 
  const handleLoad = () => {
    const autoCompleteInput = document.getElementById(name) as HTMLInputElement;
    const autocomplete = new google.maps.places.Autocomplete(autoCompleteInput);
 
    google.maps.event.addListener(autocomplete, 'place_changed', async () => {
      const place = autocomplete.getPlace();
      let optionsData = [];
      if (place) {
        const addressComponents = place.address_components;
        const formattedAddress = addressPopulationObject[name];
        if (formattedAddress.country) {
          const countryQuestion = userQuestions.find(question => question.identifier === formattedAddress.country);
          const countryDropdownFile = countryQuestion.file;
          optionsData = await loadFile(countryDropdownFile);
        }
        const responseAddress: { [key: string]: any } = {};
 
        addressComponents.forEach(a => {
          const nameComp = a.long_name;
          const short = a.short_name;
          const type = a.types[0];
 
          switch (type) {
            case 'locality':
              if (formattedAddress.city) {
                responseAddress[formattedAddress.city] = nameComp;
              }
              break;
 
            case 'administrative_area_level_1':
              if (formattedAddress.state) {
                responseAddress[formattedAddress.state] = nameComp;
              }
              break;
 
            case 'country':
              if (formattedAddress.country) {
                const selectedCountry = optionsData.find(country => country.e2b_code === a.short_name);
                responseAddress[formattedAddress.country] = selectedCountry?.value || nameComp;
                // Display both names on the page
                setCountryInfo({ longName: nameComp, shortName: short });
                // Also, log to console
                console.log('Country long name:', nameComp);
                console.log('Country short name:', short);
              }
              break;
 
            case 'postal_code':
              if (formattedAddress.postalCode) {
                responseAddress[formattedAddress.postalCode] = nameComp;
              }
              break;
          }
        });
 
        const response = {
          ...place,
          streetAddress: place.formatted_address,
          responseAddress,
        };
        onPlaceSelected(response);
      }
    });
  };
 
  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;
 
  return (
<div>
<GoogleMap onLoad={handleLoad} />
<FormInput
        {...{
          id: name,
          name,
          placeholder,
          $error: !!getErrors(name)?.length,
          disabled,
        }}
      />
      {countryInfo.longName && (
<div style={{ marginTop: '1em', background: '#f6f6f6', padding: '1em', borderRadius: '6px' }}>
<strong>Country Selected:</strong>
<div>Long Name: {countryInfo.longName}</div>
<div>Short Name: {countryInfo.shortName}</div>
</div>
      )}
</div>
  );
};
 
export default QuizFieldAutocompleteInput;